import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getStepEvent, StudentDetailsDataLayerMapperService } from '@lx/quote-tool/shared/google-analytics';
import { GoogleAnalyticsDataLayerService } from '@lx/shared/google-analytics';
import { combineLatest, map, Observable, of } from 'rxjs';
import { OnlineQuoteToolStore } from '../store';
import { GoogleAnalyticsOnlineQuoteToolQuote, GoogleAnalyticsParametersType } from './google-analytics-parameters.type';
import { QuoteDataLayerService } from './quote-data-layer.service';

/**
 * Service responsible for filling the Google Analytics dataLayer with Online Quote Tool data.
 * Implements a simpler pattern than QT Lite since Online QT has fewer steps and simpler logic.
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineQuoteToolGoogleAnalyticsDataLayerService implements GoogleAnalyticsDataLayerService<GoogleAnalyticsParametersType> {
  private readonly onlineQuoteToolStore = inject(OnlineQuoteToolStore);
  private readonly route = inject(ActivatedRoute);
  private readonly quoteDataLayerService = inject(QuoteDataLayerService);
  private readonly studentDetailsDataLayerMapperService = inject(StudentDetailsDataLayerMapperService);

  /**
   * Fills the dataLayer with current application state.
   * This observable emits whenever relevant state changes occur.
   */
  fillDataLayer(): Observable<Partial<GoogleAnalyticsParametersType>> {
    return combineLatest([of(getStepEvent(this.route)), this.buildQuoteData(), this.buildStudentDetailsData()]).pipe(
      map(([step, quotes, studentDetails]) => ({
        step,
        quotes,
        ...studentDetails,
      })),
    );
  }

  /**
   * Merges two Google Analytics parameter objects (shallow merge of top-level fields).
   * Handles the quotes array specially to avoid duplicates and keep only the last 3 quotes.
   *
   * @param target - The target object to merge into (can be null/undefined)
   * @param source - The source object to merge from
   * @returns The merged object
   */
  deepMerge(
    target: Partial<GoogleAnalyticsParametersType> | null | undefined,
    source: Partial<GoogleAnalyticsParametersType>,
  ): Partial<GoogleAnalyticsParametersType> {
    const safeTarget = target ?? {};
    const previousQuotes = safeTarget.quotes || [];
    const newQuotes = source?.quotes || [];

    // Remove duplicates: filter out previous quotes that have the same 'quote' number as new quotes
    const previousNonDuplicateQuotes = previousQuotes.filter((prevQuote) => !newQuotes.some((newQuote) => prevQuote.quote === newQuote.quote));

    // Combine previous non-duplicate quotes with new quotes
    const mergedQuotes = [...previousNonDuplicateQuotes, ...newQuotes];

    // Keep only the last 3 quotes
    const lastThreeQuotes = mergedQuotes.slice(-3);

    return {
      ...safeTarget, // Keep other fields from target
      ...source, // Override other fields with source
      quotes: lastThreeQuotes,
    };
  }

  /**
   * Builds quote data for the dataLayer.
   *
   * @returns Observable of quote data array
   * @private
   */
  private buildQuoteData(): Observable<GoogleAnalyticsOnlineQuoteToolQuote[]> {
    return combineLatest([this.quoteDataLayerService.getBaseData(), this.quoteDataLayerService.getPricesData()]).pipe(
      map(([baseData, pricesData]) => [
        {
          ...baseData,
          ...pricesData,
        },
      ]),
    );
  }

  /**
   * Builds student details data for the dataLayer.
   *
   * @returns Observable of student details data
   * @private
   */
  private buildStudentDetailsData(): Observable<Partial<GoogleAnalyticsParametersType>> {
    return this.onlineQuoteToolStore.studentDetails$.pipe(
      map((studentDetails) => this.studentDetailsDataLayerMapperService.mapStudentDetailsToDataLayer(studentDetails)),
    );
  }
}
